/**
 * The map: full-bleed, the hero of the screen.
 *
 * Leaflet 1.9.4 plus Geoman, taken from the main agricultural product, which is the owner's
 * decision of 2026-08-05: that map works and it comes over whole, basemaps included. The
 * MapLibre + Terra Draw engine it replaces is gone from the tree and from package.json.
 *
 * WITHOUT `react-leaflet`, DELIBERATELY. The source pairs Leaflet with react-leaflet 4.2.1,
 * which declares `react: ^18` as a peer; this app is on React 19.1.1, so "take it as it is"
 * was never available in any form. The choice was react-leaflet@5 (React 19, but NOT the
 * code that is proven in the source) or no react-leaflet at all. It gives exactly two things
 * — `<MapContainer>` and `useMap()` — and both are already solved here: the map is one
 * `useEffect`, and everything below it takes the instance as an argument. One dependency
 * fewer, one peer conflict fewer, and `attachDrawing` / `renderFields` / `attachFieldLabels`
 * become plain functions that are testable without React.
 *
 * The props contract is unchanged from the MapLibre version except for two swaps: `labels`
 * (which hid the Esri reference overlays) became `basemap`, because the Google hybrid tile
 * has its labels baked in and there is nothing left to hide (map/basemap.ts); and the panels
 * that used to float over the map as siblings now arrive as `controls`, one node per corner,
 * because the corners are Leaflet's to lay out (map/controls.ts). The caller names a corner;
 * it still never sees an `L.Map`.
 */

import { createPortal } from "react-dom";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";

import type { Field, FieldMarker, FieldPhoto, GeoJsonPolygon } from "../api/types";
import { T } from "../strings";
import { bboxOf, bboxUnion } from "../lib/geo";
import type { Theme } from "../hooks/useTheme";
import {
  basemapLayer,
  INITIAL_VIEW,
  MAP_MAX_ZOOM,
  MAP_MIN_ZOOM,
  MAP_PALETTE,
  TILE_FILTER,
  type BasemapId,
} from "./basemap";
import { previewLayer, type PreviewBounds } from "./preview";
import { createPanes, PANES } from "./panes";
import { toLeafletBounds } from "./latlng";
import { attachTileRetry } from "./tileRetry";
import { cornerOf, mapSlot, SLOT_CLASS, type MapCorner, type MapSlot } from "./controls";
import { attachDrawing, type DrawController } from "./geoman";
import { renderFields } from "./fields";
import { attachFieldLabels } from "./labels";
import { attachPins, draftPin, PHOTO_PIN_MIN_ZOOM, type PinLayer, type PinOutcome } from "./pins";
import { attachFileDrop } from "./drop";
import { drawShortcut } from "./shortcuts";
import { issueMarker, searchMarker, type SearchPoint } from "./markers";
import { clampPlaceZoom } from "../lib/places";
import { MIN_IMAGERY_ZOOM, type Viewport } from "../lib/viewport";
import type { DrawMode, MarkerTarget } from "../components/DrawTools";

/** If the imagery never reports `load`, the fade still has to end. Source's number. */
const PREVIEW_FADE_FALLBACK_MS = 4000;

interface Props {
  theme: Theme;
  fields: Field[];
  selectedFieldId: number | null;
  onSelectField: (fieldId: number) => void;
  mode: DrawMode;
  onDraftChange: (geometry: GeoJsonPolygon | null) => void;
  /** Increment to wipe the drawing surface (draft cleared elsewhere). `0` = first render. */
  clearToken: number;
  previewTileUrl: string | null;
  /** Field bbox from the preview response: without it the whole viewport is fetched. */
  previewBounds: PreviewBounds | null;
  /** Undo/redo availability, for the two toolbar buttons. */
  onHistoryChange: (canUndo: boolean, canRedo: boolean) => void;
  /** Handed up once the surface exists, so the toolbar buttons have something to call. */
  onDrawReady: (controller: DrawController | null) => void;
  /** Where the geometry goes wrong, in [lon, lat]. */
  issuePoints: [number, number][];
  /** Coordinate search result: fly here and drop a temporary pin. Not a field. */
  searchPoint: SearchPoint | null;
  /** Which of the two ported basemaps is showing. */
  basemap: BasemapId;
  /** Заметки ВЫБРАННОГО поля. В v1 чужие не рисуются. */
  markers: FieldMarker[];
  /**
   * Чего ждёт следующий клик по карте. `null` — ничего, карта работает как обычно.
   *
   * ДВА РЕЖИМА, А НЕ ОДИН ФЛАГ (notes-v2): клик может завести НОВУЮ заметку (тулбар) или
   * указать место СУЩЕСТВУЮЩЕЙ, у которой его не было (карточка в панели). Разница не
   * косметическая — в первом случае рождается черновик с попапом, во втором летит PATCH по
   * готовой заметке, — и выражать её булевым флагом значило бы хранить «какой именно» где-то
   * ещё.
   */
  markerTarget: MarkerTarget | null;
  /**
   * Завести заметку в точке, по которой только что кликнули. Отказ оставляет попап открытым
   * вместе с набранным текстом и накопленными файлами, печатает в нём причину, и режим
   * остаётся взведённым, чтобы следующий клик мог быть поближе.
   *
   * Ответ несёт `markerId`: файлы черновика некуда отправить, пока сервер не назвал id.
   */
  /**
   * Тык по карте в режиме тулбара: место выбрано, маркер-черновик уже стоит. Слова у заметки
   * берёт модалка создания — карта их больше не спрашивает.
   */
  onDraftPlaced: (point: { lat: number; lon: number }) => void;
  /** Снять маркер-черновик: заметку сохранили, отказали или бросили. */
  draftToken: number;
  /** Клик или Enter по маркеру: открыть модалку этой заметки. */
  onOpenNote: (markerId: number) => void;
  /** Какая заметка сейчас открыта: её маркер помечен тоном. */
  openNoteId?: number | null;
  /**
   * Указать место существующей заметке — ответ на клик в режиме `place`.
   *
   * Отдельно от `onMoveMarker`: тот отвечает на перетаскивание готового флажка и умеет
   * снэпбэк, а здесь флажка ещё нет вовсе — заметка жила без места. Отказ (дальше 500 м)
   * режим НЕ гасит: следующий клик поближе — это вторая попытка, а не новое решение.
   */
  onPlaceMarker: (markerId: number, point: { lat: number; lon: number }) => Promise<PinOutcome>;
  /** Escape в попапе черновика: точка брошена И режим покинут. */
  onExitMarkerMode?: () => void;
  /**
   * Все фотографии ВЫБРАННОГО поля. Карта раскладывает их по заметкам через `marker_id`.
   *
   * Своего знака у фотографии на карте больше НЕТ (unified-notes): точка — это заметка, а
   * фотография в неё вложена. Кадр без `marker_id` на карте не существует вовсе, и это не
   * потеря — он целиком принадлежит блоку в консоли.
   */
  photos: FieldPhoto[];
  /** Миниатюра по id: и для рамки флажка, и для ленты в попапе. Кэш держит слой. */
  photoThumb?: (photoId: number) => Promise<string>;
  /**
   * «Показать заметку» из лайтбокса и «Показать на карте» из консоли: прилететь к точке и
   * коротко её пометить.
   *
   * `token` — как у поиска: та же точка, названная дважды, обязана слетать туда дважды, а без
   * счётчика второе нажатие не отличить от первого ни одним полем.
   */
  noteFocus: { markerId: number; lat: number; lon: number; token: number } | null;
  /** «Показать на карте» из консоли: кадр по всем заметкам поля. Токен — то же, что выше. */
  notesFocus: { token: number } | null;
  /** A GeoJSON or KML file dropped on the map. The only way in since the button went. */
  onFileDrop?: (file: File) => void;
  /**
   * The panels that ride on the map, addressed by corner rather than by pixel.
   *
   * They used to be siblings of the map in a strip of their own, which is how the date bar
   * came to be painted over Leaflet's zoom buttons. Now they are rendered into `L.Control`
   * containers (map/controls.ts) and Leaflet lays the corners out. The caller names a
   * corner; which corner holds what is stated in `CONTROL_LAYOUT`.
   */
  controls?: { topLeft?: ReactNode; topRight?: ReactNode };
  /**
   * Where the map is looking, published on `moveend` only. Not on every animation frame:
   * the dated-imagery layer costs money per request, and its guards (zoom, distance,
   * debounce — lib/viewport.ts) are downstream of this callback.
   */
  onViewportChange?: (viewport: Viewport) => void;
  /**
   * How many dated-imagery layers are alive right now. Developer surface only, and passed
   * only while that surface is open: the cap of two is otherwise something you have to take
   * on trust, which is how the stack of five got there in the first place.
   */
  onPreviewLayers?: (count: number) => void;
}

/**
 * The chrome is over the map, so the map has to know it is there.
 *
 * MapLibre kept a padding inside the camera and every `fitBounds`/`flyTo` obeyed it. Leaflet
 * has no such thing: `fitBounds` takes padding per call and `flyTo` takes none at all, so
 * both are handled explicitly below. Everything here is MEASURED rather than restated —
 * `--sidebar-width` is a `clamp()`, the corner groups change height with a draft sheet, and
 * a second copy of either number would be wrong the first time anything is resized.
 *
 * Left and right are the two panels, which are siblings of the map. Top is our own corner
 * groups, which are now inside it (map/controls.ts): without that term a field flown to at
 * the top of its bbox lands underneath the date control.
 */
/** The layout table is the only source of a corner; an unknown id is a programming error. */
const corner = (id: string): MapCorner => {
  const placement = cornerOf(id);
  if (!placement) throw new Error(`MapCanvas: no corner declared for control "${id}"`);
  return placement;
};

const EDGE_GAP = 16;
/** No SIDE may be more than this much padding: past it `fitBounds` has no room left to fit. */
const MAX_PADDING_SHARE = 0.45;

const panelPadding = (map: L.Map): { top: number; bottom: number; left: number; right: number } => {
  const width = (selector: string): number =>
    document.querySelector(selector)?.getBoundingClientRect().width ?? 0;
  const corners = [...document.querySelectorAll(".leaflet-top .k-mapctl")];
  const tallest = Math.max(0, ...corners.map((element) => element.getBoundingClientRect().height));
  // Padding wider than the map makes `getBoundsZoom` fit a NEGATIVE box: `getSize()` minus the
  // padding goes below zero, the scale with it, and `getScaleZoom` takes the log of a negative
  // number — the camera arrives at `NaN` and the map stops. An open draft sheet can do it on
  // the vertical axis on its own; the two side panels do it on the horizontal one as soon as
  // the window is narrower than their sum (13 rem + 19 rem + 64 px ≈ 576 px at the tablet
  // breakpoint), which is why BOTH axes are clamped and not just the one that was noticed
  // first. A map with no measured size has nothing to protect, and clamping against 0 there
  // would quietly throw the panel padding away instead.
  const size = map.getSize();
  const cap = (available: number, wanted: number): number =>
    available > 0 ? Math.min(wanted, available * MAX_PADDING_SHARE) : wanted;
  return {
    top: cap(size.y, tallest + EDGE_GAP),
    bottom: cap(size.y, EDGE_GAP),
    left: cap(size.x, width(".k-sidebar") + 32),
    right: cap(size.x, width(".k-console") + 32),
  };
};

export function MapCanvas({
  theme,
  fields,
  selectedFieldId,
  onSelectField,
  mode,
  onDraftChange,
  clearToken,
  previewTileUrl,
  previewBounds,
  onHistoryChange,
  onDrawReady,
  issuePoints,
  searchPoint,
  basemap,
  markers,
  markerTarget,
  onDraftPlaced,
  draftToken,
  onOpenNote,
  openNoteId,
  onPlaceMarker,
  onExitMarkerMode,
  photos,
  photoThumb,
  noteFocus,
  notesFocus,
  onFileDrop,
  controls,
  onViewportChange,
  onPreviewLayers,
}: Props) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const draw = useRef<DrawController | null>(null);
  const previewLayers = useRef<L.TileLayer[]>([]);
  const onPreviewLayersRef = useRef(onPreviewLayers);
  onPreviewLayersRef.current = onPreviewLayers;
  const slotRefs = useRef<MapSlot[]>([]);
  const [ready, setReady] = useState(false);
  const [slots, setSlots] = useState<{ topLeft: HTMLElement; topRight: HTMLElement } | null>(null);

  const onDraftChangeRef = useRef(onDraftChange);
  const onSelectFieldRef = useRef(onSelectField);
  // Read inside the field-click handler, which is installed once per field list. While a
  // marker is being placed a click means "here" and nothing else — otherwise one click on a
  // neighbour's contour would both switch the field and put the marker on the old one.
  const markerModeRef = useRef(markerTarget !== null);
  markerModeRef.current = markerTarget !== null;
  const onPlaceMarkerRef = useRef(onPlaceMarker);
  onPlaceMarkerRef.current = onPlaceMarker;
  const onFileDropRef = useRef(onFileDrop);
  onFileDropRef.current = onFileDrop;
  const onDraftPlacedRef = useRef(onDraftPlaced);
  onDraftPlacedRef.current = onDraftPlaced;
  const onOpenNoteRef = useRef(onOpenNote);
  onOpenNoteRef.current = onOpenNote;
  const onExitMarkerModeRef = useRef(onExitMarkerMode);
  onExitMarkerModeRef.current = onExitMarkerMode;
  const photoThumbRef = useRef(photoThumb);
  photoThumbRef.current = photoThumb;
  const onHistoryChangeRef = useRef(onHistoryChange);
  const onDrawReadyRef = useRef(onDrawReady);
  const onViewportChangeRef = useRef(onViewportChange);
  const themeRef = useRef(theme);
  onDraftChangeRef.current = onDraftChange;
  onSelectFieldRef.current = onSelectField;
  onHistoryChangeRef.current = onHistoryChange;
  onDrawReadyRef.current = onDrawReady;
  onViewportChangeRef.current = onViewportChange;
  themeRef.current = theme;

  // --- map lifecycle ------------------------------------------------------
  useEffect(() => {
    if (!container.current || map.current) return;
    const instance = L.map(container.current, {
      center: INITIAL_VIEW.center,
      zoom: INITIAL_VIEW.zoom,
      minZoom: MAP_MIN_ZOOM,
      maxZoom: MAP_MAX_ZOOM,
      // Both are added below instead, at the corner the layout table names for them.
      attributionControl: false,
      zoomControl: false,
    });
    createPanes(instance);

    // EVERY corner comes from CONTROL_LAYOUT — Leaflet's three and our two alike — so that
    // "what is in this corner" has one answer and a test can assert ours and theirs never
    // share one. The zoom is at bottom-left because of that rule, not despite it: top-right
    // is the date bar, and the two of them aiming at the same pixels is what started this.
    // Leaflet's stock titles are English ("Zoom in"); every word on screen is Russian.
    L.control
      .zoom({ position: corner("zoom"), zoomInTitle: T.map.zoomIn, zoomOutTitle: T.map.zoomOut })
      .addTo(instance);
    L.control.scale({ metric: true, imperial: false, position: corner("scale") }).addTo(instance);
    L.control.attribution({ position: corner("attribution"), prefix: false }).addTo(instance);

    const toolSlot = mapSlot(instance, corner("tools"), SLOT_CLASS.tools);
    const imagerySlot = mapSlot(instance, corner("imagery"), SLOT_CLASS.imagery);
    slotRefs.current = [toolSlot, imagerySlot];
    setSlots({ topLeft: toolSlot.element, topRight: imagerySlot.element });

    const controller = attachDrawing(instance, MAP_PALETTE[themeRef.current], {
      onGeometry: (geometry) => onDraftChangeRef.current(geometry),
      onHistory: (canUndo, canRedo) => onHistoryChangeRef.current(canUndo, canRedo),
    });
    draw.current = controller;
    onDrawReadyRef.current(controller);
    map.current = instance;
    setReady(true);

    return () => {
      controller.stop();
      draw.current = null;
      onDrawReadyRef.current(null);
      previewLayers.current = [];
      // Slots go before the map: `map.remove()` detaches the corner containers wholesale, and
      // a control removed from a map that no longer has corners throws.
      slotRefs.current.forEach((slot) => slot.remove());
      slotRefs.current = [];
      setSlots(null);
      instance.remove();
      map.current = null;
      setReady(false);
    };
  }, []);

  // --- theme: one tone curve for both photographs -------------------------
  // The filter goes on the tile panes, so the contours and the marks — which live in other
  // panes — keep their own contrast. That was the reason the MapLibre version tinted inside
  // the style rather than filtering the canvas.
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;
    for (const pane of [PANES.basemap, PANES.preview]) {
      const element = instance.getPane(pane);
      if (element) element.style.filter = TILE_FILTER[theme];
    }
  }, [theme, ready]);

  // --- basemap ------------------------------------------------------------
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;
    const layer = basemapLayer(basemap).addTo(instance);
    // Retries are attached HERE and nowhere else: a free provider tile is worth asking for
    // again, an Earth Engine tile is not (map/tileRetry.ts).
    const detach = attachTileRetry(layer);
    return () => {
      detach();
      instance.removeLayer(layer);
    };
  }, [basemap, ready]);

  // --- saved fields -------------------------------------------------------
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;
    return renderFields(instance, fields, selectedFieldId, MAP_PALETTE[theme], (fieldId) => {
      if (markerModeRef.current) return;
      onSelectFieldRef.current(fieldId);
    });
  }, [fields, selectedFieldId, theme, ready]);

  // --- field names --------------------------------------------------------
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;
    return attachFieldLabels(instance, fields, selectedFieldId, theme);
  }, [fields, selectedFieldId, theme, ready]);

  // --- заметки выбранного поля ---------------------------------------------
  //
  // СЛОЙ СТАВИТСЯ ОДИН РАЗ, а данные в него ПУШАТСЯ (эффект ниже). Прежняя версия зависела
  // от `[markers, ready]`, то есть пересоздавала слой на каждый новый массив — а попап живёт
  // на слое. Пока заметка была просто текстом, это стоило неудобства; теперь через попап
  // добавляют фотографии, и каждая загруженная возвращается новым массивом, так что попап
  // закрывался бы сам, ровно в ответ на действие, совершённое в нём же.
  const notes = useRef<PinLayer | null>(null);
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;
    const layer = attachPins(instance, [], {
      // ЕДИНСТВЕННОЕ, что слой теперь делает по нажатию. Попапа нет: редактор у заметки один,
      // и он в модалке.
      onOpenNote: (markerId) => onOpenNoteRef.current(markerId),
      thumbUrl: (photoId) =>
        photoThumbRef.current
          ? photoThumbRef.current(photoId)
          : Promise.reject(new Error("MapCanvas: no thumbnail source")),
    });
    notes.current = layer;
    return () => {
      notes.current = null;
      layer.detach();
    };
  }, [ready]);

  useEffect(() => {
    if (!ready) return;
    notes.current?.update(markers, photos);
  }, [markers, photos, ready]);

  // Какая заметка открыта в модалке: её маркер помечен тоном и поднят над соседями.
  useEffect(() => {
    if (!ready) return;
    notes.current?.setOpen(openNoteId ?? null);
  }, [openNoteId, ready]);

  // --- placing one ---------------------------------------------------------
  //
  // The flag appears where the click landed BEFORE the server has agreed to it: how far
  // outside the contour is too far is the server's rule, and a point that vanishes for a
  // second while a request flies would read as a click that missed. If the answer is a
  // refusal the draft goes and the reason is printed under the toolbar, mode still on.
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready || markerTarget === null) return;
    const container = instance.getContainer();
    container.classList.add("k-map--marking");
    let draft: L.Marker | null = null;

    const dropDraft = (): void => {
      if (draft) instance.removeLayer(draft);
      draft = null;
    };

    /**
     * УКАЗАТЬ МЕСТО СУЩЕСТВУЮЩЕЙ ЗАМЕТКЕ. Черновика здесь нет и попапа тоже: заметка уже
     * написана, спрашивать нечего — клик отвечает на единственный оставшийся вопрос. Удача
     * помечает точку вспышкой, и ту же вспышку получает карточка в панели: одно действие, две
     * поверхности, один ответ. Отказ режим не гасит — целятся ещё раз, поближе.
     */
    const place = (noteId: number, event: L.LeafletMouseEvent): void => {
      const { lat, lng } = event.latlng;
      /**
       * КЛИК ПРИЗЕМЛЯЕТСЯ ВИДИМО ДО ОТВЕТА СЕРВЕРА. Насколько далеко за контуром «слишком
       * далеко» — правило сервера, и точка, пропадающая на время запроса, читается как
       * промахнувшийся клик. Маркер временный и глухой: спрашивать нечего, заметка уже
       * написана — место у неё и указывают.
       */
      const pending = draftPin(lat, lng).addTo(instance);
      void onPlaceMarkerRef.current(noteId, { lat, lon: lng }).then((result) => {
        // Снимается в обоих исходах: при удаче настоящий маркер приходит из состояния, при
        // отказе показывать место, которого на сервере нет, — враньё.
        instance.removeLayer(pending);
        if (!result.ok) return;
        notes.current?.highlight([noteId]);
      });
    };

    const onClick = (event: L.LeafletMouseEvent): void => {
      if (markerTarget.kind === "place") {
        place(markerTarget.noteId, event);
        return;
      }
      dropDraft();
      const { lat, lng } = event.latlng;
      /**
       * МАРКЕР ПОЯВЛЯЕТСЯ НЕМЕДЛЕННО, ДО ОТВЕТА СЕРВЕРА, и попапа у него нет. Точка может
       * лежать за контуром, и насколько далеко — знает только сервер; клик обязан
       * приземлиться видимо, иначе он читается как промах. Слова у заметки берёт модалка,
       * которая открывается тем же жестом.
       */
      const layer = draftPin(lat, lng).addTo(instance);
      draft = layer;
      onDraftPlacedRef.current({ lat, lon: lng });
    };

    instance.on("click", onClick);
    // Владелец сказал снять черновик: заметку сохранили, отказали или бросили. Токен, а не
    // булево, — второй отказ подряд обязан снять маркер так же, как первый.
    dropRef.current = dropDraft;
    return () => {
      instance.off("click", onClick);
      container.classList.remove("k-map--marking");
      dropDraft();
      dropRef.current = null;
    };
  }, [markerTarget, ready]);

  const dropRef = useRef<(() => void) | null>(null);
  const seenDraftToken = useRef(draftToken);
  useEffect(() => {
    if (draftToken === seenDraftToken.current) return;
    seenDraftToken.current = draftToken;
    dropRef.current?.();
  }, [draftToken]);

  // --- a contour file dropped on the map ----------------------------------
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;
    return attachFileDrop(instance.getContainer(), (file) => onFileDropRef.current?.(file));
  }, [ready]);

  // --- fit to the selected field, or to everything ------------------------
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready || fields.length === 0) return;
    const selected = fields.find((field) => field.id === selectedFieldId);
    const box = selected
      ? bboxOf(selected.geometry)
      : bboxUnion(fields.map((field) => bboxOf(field.geometry)));
    if (!box) return;
    const padding = panelPadding(instance);
    instance.flyToBounds(toLeafletBounds(box), {
      paddingTopLeft: [padding.left, padding.top],
      paddingBottomRight: [padding.right, padding.bottom],
      maxZoom: 15,
      duration: 0.6,
    });
  }, [selectedFieldId, fields, ready]);

  // --- draw mode ----------------------------------------------------------
  useEffect(() => {
    if (!ready) return;
    draw.current?.setMode(mode);
  }, [mode, ready]);

  useEffect(() => {
    if (!ready || clearToken === 0) return;
    draw.current?.clear();
  }, [clearToken, ready]);

  // Undo/redo from outside the map; see map/shortcuts.ts.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const shortcut = drawShortcut(event);
      if (!shortcut) return;
      event.preventDefault();
      if (shortcut === "undo") draw.current?.undo();
      else draw.current?.redo();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // --- geometry problems, marked where they are ---------------------------
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;
    const markers = issuePoints.map((point) => issueMarker(point).addTo(instance));
    return () => markers.forEach((marker) => instance.removeLayer(marker));
  }, [issuePoints, ready]);

  // --- coordinate search --------------------------------------------------
  // `flyTo` takes no padding, so the correction is applied to the centre by hand. Without it
  // the pin lands under the console — the map arrives, just not where it was asked to.
  const flyToPoint = useCallback((instance: L.Map, lat: number, lon: number, zoom: number) => {
    const padding = panelPadding(instance);
    const offset = L.point((padding.left - padding.right) / 2, (padding.top - padding.bottom) / 2);
    const centre = instance.unproject(instance.project([lat, lon], zoom).subtract(offset), zoom);
    instance.flyTo(centre, zoom, { duration: 0.9 });
  }, []);

  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready || !searchPoint) return;
    // A place with an extent is flown to two steps CLOSER than its own frame: fitting a city
    // gives a useless overview, and below zoom 14 the imagery layer would go out silently
    // (lib/places.ts). `getBoundsZoom` is measured here because it depends on the viewport.
    const zoom = searchPoint.box
      ? clampPlaceZoom(
          instance.getBoundsZoom(
            toLeafletBounds([
              searchPoint.box.west,
              searchPoint.box.south,
              searchPoint.box.east,
              searchPoint.box.north,
            ]),
          ) + 2,
        )
      : (searchPoint.zoom ?? MIN_IMAGERY_ZOOM);
    flyToPoint(instance, searchPoint.lat, searchPoint.lon, zoom);
    const marker = searchMarker(searchPoint).addTo(instance);
    return () => {
      instance.removeLayer(marker);
    };
  }, [searchPoint, ready, flyToPoint]);

  // «Показать заметку» из лайтбокса. Ближе, чем стоит камера, но не дальше: летим к заметке С
  // ФОТОГРАФИЯМИ, и ниже порога миниатюры она нарисована обычным флажком — то есть мы
  // прилетели бы показывать не тот знак, о котором спрашивали.
  //
  // Попап открывается ПОСЛЕ перелёта, а не до: Leaflet держит его на экране всю анимацию, и
  // открытый заранее он проезжает через полкарты вместе с камерой.
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready || noteFocus === null) return;
    flyToPoint(
      instance,
      noteFocus.lat,
      noteFocus.lon,
      Math.max(instance.getZoom(), PHOTO_PIN_MIN_ZOOM),
    );
    const markerId = noteFocus.markerId;
    // ТОЛЬКО ВСПЫШКА, БЕЗ ОТКРЫТИЯ. «Показать на карте» отвечает на «где эта заметка», а не
    // «покажи её целиком»: окно, прыгнувшее в лицо в ответ на этот вопрос, закрыло бы ровно
    // тот вид, ради которого его задавали.
    const arrive = (): void => {
      notes.current?.highlight([markerId]);
    };
    instance.once("moveend", arrive);
    return () => {
      instance.off("moveend", arrive);
    };
  }, [noteFocus, ready, flyToPoint]);

  // «Показать на карте» из консоли: другой вопрос и потому другой кадр. Спрашивают не «где
  // вот эта», а «где они все», поэтому кадр собирается по ВСЕМ заметкам, попап не открывается
  // ни у одной (открыть одну значило бы ответить не на тот вопрос), и мигают тоже все.
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready || notesFocus === null) return;
    // Кадр собирается ТОЛЬКО по заметкам с местом: у заметки без него координат нет вовсе, и
    // включить её в границы значило бы тянуть кадр в точку [0, 0] где-то в Атлантике.
    const placed = markers.filter(
      (marker): marker is FieldMarker & { lat: number; lon: number } =>
        marker.lat !== null && marker.lon !== null,
    );
    if (placed.length === 0) return;
    const padding = panelPadding(instance);
    instance.flyToBounds(L.latLngBounds(placed.map((marker) => [marker.lat, marker.lon])), {
      paddingTopLeft: [padding.left, padding.top],
      paddingBottomRight: [padding.right, padding.bottom],
      maxZoom: PHOTO_PIN_MIN_ZOOM + 2,
      duration: 0.6,
    });
    notes.current?.highlight(placed.map((marker) => marker.id));
    // `markers` намеренно НЕ в зависимостях: кадр собирают по нажатию, а не по приходу
    // данных, иначе каждая загруженная фотография уводила бы карту с того места, где на неё
    // смотрят. Токен — единственное, что означает «нажали».
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesFocus, ready]);

  // --- where the map is looking -------------------------------------------
  // `moveend`, not `move`: the dated-imagery layer pays per request, and publishing every
  // frame would defeat the 2 s debounce downstream before it ever ran. Leaflet fires
  // `moveend` once after inertia settles, not per inertia step.
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;
    const publish = (): void => {
      const bounds = instance.getBounds();
      const center = instance.getCenter();
      onViewportChangeRef.current?.({
        bbox: [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
        center: [center.lng, center.lat],
        zoom: instance.getZoom(),
      });
    };
    publish();
    instance.on("moveend", publish);
    return () => {
      instance.off("moveend", publish);
    };
  }, [ready]);

  // --- satellite preview --------------------------------------------------
  // Cross-faded rather than swapped: a hard replace flashes the basemap white between two
  // dates, and stepping through a week of imagery is the main gesture this control exists
  // for. The new layer comes up at opacity 0 and every older one is dropped once it paints.
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;
    if (!previewTileUrl) {
      previewLayers.current.forEach((layer) => instance.removeLayer(layer));
      previewLayers.current = [];
      onPreviewLayersRef.current?.(0);
      return;
    }
    // The bbox is what keeps the request count down — see map/preview.ts.
    const next = previewLayer(previewTileUrl, previewBounds).addTo(instance);
    // AT MOST TWO LIVE LAYERS, and they are dropped BEFORE the new one paints rather than
    // after. Waiting for `load` was the real "too many tiles": on a slow line a pass through
    // five dates left five layers alive, each still pulling its ~40 tiles, i.e. up to 200
    // simultaneous Earth Engine renders. A cross-fade needs exactly two, so everything older
    // than the immediate predecessor goes now.
    const stale = previewLayers.current.slice(0, -1);
    stale.forEach((layer) => instance.removeLayer(layer));
    previewLayers.current = [...previewLayers.current.slice(-1), next];
    onPreviewLayersRef.current?.(previewLayers.current.length);

    const promote = (): void => {
      next.setOpacity(1);
      // EVERY predecessor, not just the one before it. The source removes a single previous
      // layer, so stepping through dates faster than the tiles load leaves layers — and
      // their requests — stacked on the map for good.
      const older = previewLayers.current.slice(0, previewLayers.current.indexOf(next));
      older.forEach((layer) => instance.removeLayer(layer));
      previewLayers.current = previewLayers.current.filter((layer) => !older.includes(layer));
      onPreviewLayersRef.current?.(previewLayers.current.length);
    };
    next.once("load", promote);
    const fallback = setTimeout(promote, PREVIEW_FADE_FALLBACK_MS);
    return () => clearTimeout(fallback);
  }, [previewTileUrl, previewBounds, ready]);

  return (
    <div className="k-map" ref={container} data-testid="map-canvas">
      {slots && controls?.topLeft ? createPortal(controls.topLeft, slots.topLeft) : null}
      {slots && controls?.topRight ? createPortal(controls.topRight, slots.topRight) : null}
    </div>
  );
}
